import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { SearchSelect } from '@/components/ui/search-select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Loader2, RotateCcw, Save, SlidersHorizontal, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fieldLabel } from './lotPresentation';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import type { CorrectionField, CorrectionSchema, MigrationRecord } from '../types';

type SaveBusy = 'save' | 'saveRetry' | null;

interface SmartCorrectionFormProps {
  record: MigrationRecord;
  /** État d'occupation piloté par le parent (partagé avec l'éditeur avancé). */
  busy: SaveBusy;
  /** Enregistre le patch (clés modifiées uniquement) puis relance si demandé. */
  onSave: (patch: Record<string, unknown>, thenRetry: boolean) => void;
  onCancel: () => void;
  /** Bascule vers l'éditeur clé/valeur brut (filet de sécurité). */
  onOpenAdvanced: () => void;
}

/** Valeur d'un champ dans l'état du formulaire (string, ou liste pour un multiselect). */
type FieldValue = string | string[];

/** Découpe un CSV d'identifiants en liste (ignore vides et « 0 »). */
function splitCsv(csv: string): string[] {
  return csv
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '' && s !== '0');
}

/** Initialise la valeur d'un champ à partir du schéma. */
function initialValue(field: CorrectionField): FieldValue {
  if (field.widget === 'multiselect') {
    return splitCsv(field.value == null ? '' : String(field.value));
  }
  if (field.widget === 'boolean') {
    return field.value === true || field.value === 'true' ? 'true' : 'false';
  }
  return field.value == null ? '' : String(field.value);
}

/**
 * Convertit la valeur du formulaire vers la forme attendue dans `mappedData`
 * (celle que consomme le loader) : nombre, booléen, id source, CSV, ou `null`
 * (le backend supprime alors la clé).
 */
function coerce(field: CorrectionField, raw: FieldValue): unknown {
  if (field.widget === 'multiselect') {
    const list = raw as string[];
    return list.length === 0 ? null : list.join(',');
  }
  const text = (raw as string).trim();
  if (field.widget === 'boolean') {
    return text === 'true';
  }
  if (text === '') return null;
  if (field.widget === 'number') {
    return Number.isNaN(Number(text)) ? text : Number(text);
  }
  if (field.widget === 'select') {
    // Les ids source numériques sont réinjectés en nombre (comme en V1).
    return /^\d+$/.test(text) ? Number(text) : text;
  }
  return text;
}

/** Normalise une valeur pour comparer l'état courant à l'original (diff du patch). */
function normalize(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return [...value].map(String).sort().join(',');
  if (typeof value === 'string') return splitCsv(value).sort().join(',') || value;
  return String(value);
}

/**
 * Formulaire de correction CONVIVIAL d'un record en échec.
 *
 * Au lieu de l'éditeur clé/valeur brut (noms techniques + identifiants), chaque
 * champ est rendu avec un widget lisible piloté par le backend
 * (`correction-schema`) : texte, nombre, interrupteur, et surtout des LISTES
 * DÉROULANTES pour les champs référence (enseignant principal, salle,
 * niveaux…) où l'on choisit un libellé (« Marie Dupont ») — jamais un id.
 *
 * L'enregistrement passe par le PATCH mapped-data existant (la value d'une
 * option EST l'id source V1 attendu par le loader) ; seules les clés modifiées
 * sont envoyées.
 */
export function SmartCorrectionForm({
  record,
  busy,
  onSave,
  onCancel,
  onOpenAdvanced,
}: SmartCorrectionFormProps) {
  const [schema, setSchema] = useState<CorrectionSchema | null>(null);
  // `loading` démarre à true : le composant est (re)monté à chaque ouverture du
  // formulaire, avec un `record.id` stable — inutile de le repositionner dans
  // l'effet (ce qui déclencherait un rendu en cascade, cf. react-hooks).
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, FieldValue>>({});

  useEffect(() => {
    let cancelled = false;
    migrationV1V2Api
      .getRecordCorrectionSchema(record.id)
      .then((s) => {
        if (cancelled) return;
        setSchema(s);
        const next: Record<string, FieldValue> = {};
        for (const field of s.fields) next[field.key] = initialValue(field);
        setValues(next);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const err = e as { response?: { data?: { error?: string } }; message?: string };
        setLoadError(err.response?.data?.error ?? err.message ?? String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [record.id]);

  const original = record.mappedData ?? {};

  const editableFields = useMemo(
    () => (schema?.fields ?? []).filter((f) => !f.readOnly),
    [schema]
  );

  /** Champs requis encore vides (bloquent une relance utile). */
  const missingRequired = useMemo(
    () =>
      editableFields.filter((f) => {
        if (!f.required) return false;
        const v = values[f.key];
        return Array.isArray(v) ? v.length === 0 : (v ?? '').toString().trim() === '';
      }),
    [editableFields, values]
  );

  const buildPatch = (): Record<string, unknown> => {
    const patch: Record<string, unknown> = {};
    for (const field of schema?.fields ?? []) {
      if (field.readOnly) continue;
      const coerced = coerce(field, values[field.key] ?? (field.widget === 'multiselect' ? [] : ''));
      if (normalize(coerced) !== normalize(original[field.key])) {
        patch[field.key] = coerced;
      }
    }
    return patch;
  };

  const setValue = (key: string, value: FieldValue) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  if (loading) {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/30 p-6 flex items-center justify-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Préparation du formulaire de correction…
      </div>
    );
  }

  if (loadError || !schema) {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/30 p-3 space-y-3">
        <p className="text-sm text-red-600 dark:text-red-400">
          {loadError ?? 'Impossible de préparer le formulaire de correction.'}
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onOpenAdvanced}>
            <SlidersHorizontal className="h-3.5 w-3.5" />
            Édition avancée
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>
            <X className="h-3.5 w-3.5" />
            Annuler
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/30 p-3 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-gray-600 dark:text-gray-300">
          Choisissez la bonne valeur pour chaque champ, puis relancez la ligne.
          Les champs liés (enseignant, salle, niveau…) proposent les valeurs déjà
          migrées de l'école. ⚠️ Une relance du lot entier ré-extrait la V1 et
          écrase ces corrections — relancez la ligne juste après l'enregistrement.
        </p>
        <button
          type="button"
          onClick={onOpenAdvanced}
          className="shrink-0 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline decoration-dotted"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Avancé
        </button>
      </div>

      {editableFields.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          Aucun champ éditable pour ce lot. Utilisez l'édition avancée.
        </p>
      ) : (
        <div className="rounded-lg border bg-white dark:bg-slate-900 divide-y dark:divide-slate-700">
          {editableFields.map((field) => {
            const isMissing = missingRequired.includes(field);
            return (
              <div
                key={field.key}
                className="flex flex-col gap-1.5 px-3 py-2.5 sm:flex-row sm:items-center sm:gap-4"
              >
                <label className="sm:w-1/3 shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {fieldLabel(field.key)}
                  {field.required && <span className="text-red-500 ml-0.5">*</span>}
                  {isMissing && (
                    <span className="block text-[11px] font-normal text-red-500">
                      Valeur requise
                    </span>
                  )}
                </label>
                <div className="flex-1 min-w-0">
                  <FieldWidget
                    field={field}
                    value={values[field.key] ?? (field.widget === 'multiselect' ? [] : '')}
                    onChange={(v) => setValue(field.key, v)}
                    invalid={isMissing}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy !== null}>
          <X className="h-3.5 w-3.5" />
          Annuler
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onSave(buildPatch(), false)}
          disabled={busy !== null}
        >
          {busy === 'save' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="h-3.5 w-3.5" />
          )}
          Enregistrer
        </Button>
        <Button size="sm" onClick={() => onSave(buildPatch(), true)} disabled={busy !== null}>
          {busy === 'saveRetry' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCcw className="h-3.5 w-3.5" />
          )}
          Enregistrer et relancer
        </Button>
      </div>
    </div>
  );
}

interface FieldWidgetProps {
  field: CorrectionField;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  invalid: boolean;
}

/** Rend le widget adapté au type de champ (référence → liste déroulante lisible). */
function FieldWidget({ field, value, onChange, invalid }: FieldWidgetProps) {
  const options = field.options ?? [];

  if (field.widget === 'select') {
    // Option « Aucun » pour vider un champ facultatif.
    const opts = field.required
      ? options
      : [{ value: '', label: '— Aucun —' }, ...options];
    return (
      <SearchSelect
        options={opts}
        value={value as string}
        onValueChange={onChange}
        placeholder="Sélectionner…"
        searchPlaceholder="Rechercher…"
        className={cn(invalid && 'border-red-400 focus:ring-red-400')}
        contentClassName="min-w-[18rem]"
      />
    );
  }

  if (field.widget === 'multiselect') {
    return (
      <MultiSelect
        options={options}
        selected={value as string[]}
        onChange={onChange}
        placeholder="Sélectionner…"
        searchPlaceholder="Rechercher…"
      />
    );
  }

  if (field.widget === 'boolean') {
    const checked = value === 'true';
    return (
      <div className="flex items-center gap-2">
        <Switch checked={checked} onCheckedChange={(c) => onChange(c ? 'true' : 'false')} />
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {checked ? 'Oui' : 'Non'}
        </span>
      </div>
    );
  }

  return (
    <Input
      className={cn('h-9 text-sm', invalid && 'border-red-400 focus-visible:ring-red-400')}
      type={field.widget === 'number' ? 'number' : 'text'}
      value={value as string}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
